/**
 * Transition Rules: EnergyPlus 25.1 to 25.2
 *
 * Based on the official EnergyPlus transition rules from CreateNewIDFUsingRulesV25_2_0.f90
 */

import type { VersionTransition, ObjectTransitionRule } from '../types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';

/**
 * Helper to create a rule that inserts a new blank field at position 2 (Availability Schedule)
 * Pattern: copy field 0, insert blank at 1, shift rest
 */
function createAvailabilityScheduleInsertRule(className: string): ObjectTransitionRule {
  return {
    sourceClass: className,
    targetClass: className,
    fieldMappings: [
      // Copy field 0 (Name)
      { sourceIndex: 0, targetIndex: 0, type: 'copy' },
      // Insert blank field at position 1 (Availability Schedule Name)
      { sourceIndex: -1, targetIndex: 1, type: 'add_default', defaultValue: '' },
      // Shift remaining fields (assuming up to 50 fields)
      ...createShiftedCopyMappings(1, 2, 49),
    ],
  };
}

/**
 * Create the transition definition for 25.1 to 25.2
 */
export function createTransition_25_1_to_25_2(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // ============================================================================
  // Object Transition Rules
  // ============================================================================

  /**
   * AirLoopHVAC:UnitaryHeatPump:AirToAir:MultiSpeed
   * Insert blank field at position 12 (after field 11)
   * Fields 12+ shift to 13+
   */
  objectRules.set('AIRLOOPHVAC:UNITARYHEATPUMP:AIRTOAIR:MULTISPEED', {
    sourceClass: 'AirLoopHVAC:UnitaryHeatPump:AirToAir:MultiSpeed',
    targetClass: 'AirLoopHVAC:UnitaryHeatPump:AirToAir:MultiSpeed',
    fieldMappings: [
      // Copy fields 0-10 as-is
      ...createCopyMappings(0, 11),
      // Insert blank at position 11
      { sourceIndex: -1, targetIndex: 11, type: 'add_default', defaultValue: '' },
      // Original field 11 is skipped (was old field that gets removed)
      // Shift fields 12+ to 12+ (no change in index for fields after the skip)
      ...createShiftedCopyMappings(12, 12, 40),
    ],
  });

  /**
   * Many coil objects get a new Availability Schedule Name field at position 1
   * These all follow the same pattern: insert blank at position 1
   */
  const coilsWithAvailabilitySchedule = [
    'Coil:Cooling:WaterToAirHeatPump:VariableSpeedEquationFit',
    'Coil:Heating:WaterToAirHeatPump:VariableSpeedEquationFit',
    'Coil:Cooling:DX:VariableSpeed',
    'Coil:Heating:DX:VariableSpeed',
    'Coil:WaterHeating:AirToWaterHeatPump:VariableSpeed',
    'Coil:Cooling:WaterToAirHeatPump:EquationFit',
    'Coil:Heating:WaterToAirHeatPump:EquationFit',
    'Coil:Cooling:WaterToAirHeatPump:ParameterEstimation',
    'Coil:Heating:WaterToAirHeatPump:ParameterEstimation',
    'Coil:WaterHeating:AirToWaterHeatPump:Pumped',
    'Coil:WaterHeating:AirToWaterHeatPump:Wrapped',
  ];

  for (const coilClass of coilsWithAvailabilitySchedule) {
    objectRules.set(coilClass.toUpperCase(), createAvailabilityScheduleInsertRule(coilClass));
  }

  /**
   * GroundHeatExchanger:System
   * If CurArgs > 10, insert 2 blank fields at positions 10 and 11
   * Otherwise pass through unchanged
   */
  objectRules.set('GROUNDHEATEXCHANGER:SYSTEM', {
    sourceClass: 'GroundHeatExchanger:System',
    targetClass: 'GroundHeatExchanger:System',
    fieldMappings: [
      // Copy fields 0-9 as-is
      ...createCopyMappings(0, 10),
      // Insert 2 blank fields at positions 10 and 11
      { sourceIndex: -1, targetIndex: 10, type: 'add_default', defaultValue: '' },
      { sourceIndex: -1, targetIndex: 11, type: 'add_default', defaultValue: '' },
      // Shift remaining fields (10+ becomes 12+)
      ...createShiftedCopyMappings(10, 12, 40),
    ],
  });

  return {
    fromVersion: { major: 25, minor: 1 },
    toVersion: { major: 25, minor: 2 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
